/**
 * The standard undo and redo keys for a root shell: Cmd-Z and Shift-Cmd-Z on
 * a Mac, Ctrl-Z and Ctrl-Y or Ctrl-Shift-Z elsewhere, each claimed from the
 * browser. A key typed into a field is left to the field, so a number input
 * keeps its own text undo. A read-only shell listens for neither: undo and
 * redo are edits, which a read-only shell never makes, so the keys stay with
 * the host page.
 */

import { useEffect } from 'react';
import { isTypingInField } from './isTypingInField';

/** What the keys drive: the shell state's own undo and redo. */
interface UndoRedo {
  undo: () => void;
  redo: () => void;
}

/** The command a key press asks for, or null for any other key and for keys typed into a field. */
const shortcutCommand = (event: KeyboardEvent): keyof UndoRedo | null => {
  if (!(event.metaKey || event.ctrlKey) || event.altKey || isTypingInField(event)) return null;
  const key = event.key.toLowerCase();
  if (key === 'y') return 'redo';
  if (key === 'z') return event.shiftKey ? 'redo' : 'undo';
  return null;
};

export function useUndoRedoShortcuts({ undo, redo }: UndoRedo, readOnly: boolean): void {
  useEffect(() => {
    if (readOnly) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      const command = shortcutCommand(event);
      if (!command) return;
      event.preventDefault();
      (command === 'redo' ? redo : undo)();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo, readOnly]);
}
